import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from 'antd';
import '../styles/RecurringDatePicker.css';
import dayjs from 'dayjs';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { getSafeTimezone } from '../utils/timezoneUtils';

const RecurringDatePicker = (props) => {
  // CRITICAL: Initialize local state from props to prevent callback from firing with undefined
  // This ensures the first render already has the correct values from parent
  const [localStartValue, setLocalStartValue] = useState(() => props.startValue ?? undefined);
  const [localEndValue, setLocalEndValue] = useState(() => props.endValue ?? undefined);
  const [localTimeValue, setLocalTimeValue] = useState(() => props.timeValue ?? undefined);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);

  // Track previous prop values to detect actual changes
  // Initialize with current prop values to prevent false-positive changes on first render
  const prevPropsRef = useRef({
    startValue: props.startValue,
    endValue: props.endValue,
    timeValue: props.timeValue,
  });

  // Track if component has mounted - only fire callbacks after mount to prevent
  // overwriting parent state with initial (potentially stale) values
  const hasMountedRef = useRef(false);

  const dateFormat = 'MM/DD/YYYY';

  // Extract theme prop (default to payment_form for backward compatibility)
  const theme = props.theme || 'payment_form';

  // Extract timezone prop (default to browser timezone) with safe validation
  const timezone = getSafeTimezone(props.timezone);

  // Helper function to get current hour in the specified timezone
  const getCurrentHourInTimezone = () => {
    try {
      const now = new Date();
      
      // Validate the date object before formatting
      if (!now || isNaN(now.getTime())) {
        console.error('[getCurrentHourInTimezone] Invalid date object');
        return new Date().getHours();
      }
      
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        hour12: false,
      });
      
      const hourStr = formatter.format(now);
      const parsedHour = parseInt(hourStr, 10);
      
      // Validate the parsed hour
      if (isNaN(parsedHour)) {
        console.error('[getCurrentHourInTimezone] Invalid parsed hour:', hourStr);
        return new Date().getHours();
      }
      
      // ECMA-402 allows Intl.DateTimeFormat to return "24" for midnight
      // Apply modulo to normalize to 0-23 range
      return parsedHour % 24;
    } catch (error) {
      // Catch any errors including:
      // - SyntaxError: The string did not match the expected pattern
      // - RangeError: Invalid timezone
      // - TypeError: Invalid date operations
      console.error('[getCurrentHourInTimezone] Error formatting date:', error);
      // Fallback to current hour in local timezone
      return new Date().getHours();
    }
  };

  // Helper function to get current normalized time in the specified timezone
  const getCurrentNormalizedTime = () => {
    const currentHour = getCurrentHourInTimezone();
    if (currentHour >= 23) return '00:00';
    const normalizedHour = currentHour + 1;
    return `${normalizedHour.toString().padStart(2, '0')}:00`;
  };

  // Helper function to check if nextChargeDate is today
  const isNextChargeDateToday = () => {
    if (!localStartValue) return true;
    const today = dayjs().startOf('day');
    const startDate = dayjs(localStartValue).startOf('day');
    return startDate.isSame(today);
  };

  // Helper function to get the appropriate time value to display
  const getDisplayTimeValue = () => {
    if (localTimeValue) return localTimeValue;
    if (!localStartValue) return 'now';
    if (isNextChargeDateToday()) return 'now';
    return getCurrentNormalizedTime();
  };

  // Helper function to generate available time options (using merchant timezone)
  const getAvailableTimeOptions = () => {
    const options = [];
    const currentHour = getCurrentHourInTimezone();

    if (!localStartValue || isNextChargeDateToday()) {
      const nextNormalizedHour = currentHour + 1;
      for (let hour = nextNormalizedHour; hour <= 23; hour++) {
        options.push(`${hour.toString().padStart(2, '0')}:00`);
      }
    } else {
      for (let hour = 0; hour <= 23; hour++) {
        options.push(`${hour.toString().padStart(2, '0')}:00`);
      }
    }

    return options;
  };

  // Pure function: snap an end date to align with start date based on frequency.
  // Returns the adjusted dayjs value, or null if no adjustment needed.
  const snapEndDate = (endVal) => {
    if (endVal === null || endVal === undefined || endVal === 'INFINITY') {
      return null;
    }

    let startingPosition = (localStartValue === null || localStartValue === undefined)
      ? dayjs().startOf('day')
      : dayjs(localStartValue).startOf('day');
    const endingPosition = dayjs(endVal).startOf('day');

    let newEndValue = endingPosition;

    if (props.timePicker === 'year' && endingPosition.month() !== startingPosition.month()) {
      newEndValue = newEndValue.month(startingPosition.month());
    }

    if (props.timePicker === 'week') {
      let dayOfWeek = dayjs(endVal).day();
      let dayOfWeekStart = startingPosition.day();
      let difference = dayOfWeekStart - dayOfWeek;
      newEndValue = dayjs(endVal).add(difference, 'day');
    }

    if (props.timePicker === 'month' || props.timePicker === 'year') {
      let dayOfMonth = startingPosition.date();
      let endOfMonth = newEndValue.endOf('month').date();
      if (dayOfMonth > endOfMonth) {
        dayOfMonth = endOfMonth;
      }
      newEndValue = newEndValue.date(dayOfMonth);
    }

    if (newEndValue.isBefore(startingPosition) || newEndValue.isSame(startingPosition)) {
      newEndValue = dayjs(startingPosition);
      if (props.timePicker === 'day') {
        newEndValue = newEndValue.add(1, 'day');
      }
      if (props.timePicker === 'week') {
        newEndValue = newEndValue.add(1, 'week');
      }
      if (props.timePicker === 'month') {
        newEndValue = newEndValue.add(1, 'month');
      }
      if (props.timePicker === 'year') {
        newEndValue = newEndValue.add(1, 'year');
      }
    }

    if (newEndValue.format('YYYY-MM-DD') !== endingPosition.format('YYYY-MM-DD')) {
      return newEndValue;
    }
    return null;
  };

  const resetEndValue = () => {
    const snapped = snapEndDate(localEndValue);
    if (snapped) {
      setLocalEndValue(snapped);
    }
  };

  // Sync props to local state only when props actually change from parent
  useEffect(() => {
    const prevStartValue = prevPropsRef.current.startValue;
    const currentStartValue = props.startValue;

    // HOTFIX: Skip sync if local state already has the same value
    // Prevents overwriting user's fresh selection with stale parent prop
    // Based on React "Ignore Stale" pattern (data-driven, not timing-based)
    if (localStartValue !== undefined && localStartValue !== null) {
      if (
        currentStartValue !== null &&
        currentStartValue !== undefined &&
        dayjs(localStartValue).isSame(currentStartValue, 'day')
      ) {
        // Already in sync, skip update
        prevPropsRef.current.startValue = currentStartValue;
        return;
      }
    }

    const propChanged =
      (prevStartValue === undefined || prevStartValue === null) !==
        (currentStartValue === undefined || currentStartValue === null) ||
      (prevStartValue &&
        currentStartValue &&
        !dayjs(prevStartValue).isSame(currentStartValue, 'day'));

    if (propChanged) {
      prevPropsRef.current.startValue = currentStartValue;
      if (currentStartValue === null || currentStartValue === undefined) {
        setLocalStartValue(undefined);
      } else {
        setLocalStartValue(currentStartValue);
      }
    }
  }, [props.startValue, localStartValue]);

  useEffect(() => {
    const prevEndValue = prevPropsRef.current.endValue;
    const currentEndValue = props.endValue;

    // HOTFIX: Skip sync if local state already has the same value
    // Prevents overwriting user's fresh selection with stale parent prop
    // Based on React "Ignore Stale" pattern (data-driven, not timing-based)
    if (localEndValue !== undefined && localEndValue !== null && localEndValue !== 'INFINITY') {
      if (
        currentEndValue !== null &&
        currentEndValue !== undefined &&
        currentEndValue !== 'INFINITY' &&
        dayjs(localEndValue).isSame(currentEndValue, 'day')
      ) {
        // Already in sync, skip update
        prevPropsRef.current.endValue = currentEndValue;
        return;
      }
    }

    const propChanged =
      prevEndValue !== currentEndValue &&
      (prevEndValue === undefined ||
        prevEndValue === null ||
        currentEndValue === undefined ||
        currentEndValue === null ||
        prevEndValue === 'INFINITY' ||
        currentEndValue === 'INFINITY' ||
        !dayjs(prevEndValue).isSame(currentEndValue, 'day'));

    if (propChanged) {
      prevPropsRef.current.endValue = currentEndValue;
      if (currentEndValue === null || currentEndValue === undefined) {
        setLocalEndValue(undefined);
      } else {
        setLocalEndValue(currentEndValue);
      }
    }
  }, [props.endValue, localEndValue]);

  useEffect(() => {
    const prevTimeValue = prevPropsRef.current.timeValue;
    const currentTimeValue = props.timeValue;

    if (prevTimeValue !== currentTimeValue) {
      prevPropsRef.current.timeValue = currentTimeValue;
      if (currentTimeValue === null || currentTimeValue === undefined) {
        setLocalTimeValue(undefined);
      } else {
        setLocalTimeValue(currentTimeValue);
      }
    }
  }, [props.timeValue]);

  // Mark component as mounted after first render
  // This ensures callbacks only fire for user-initiated changes, not initial prop sync
  useEffect(() => {
    // Mark as mounted on next tick to ensure initial state is set
    hasMountedRef.current = true;
  }, []);

  // Handle end date adjustments when start value changes
  // CRITICAL: Only run when localStartValue changes, NOT when localEndValue changes.
  // resetEndValue is excluded from deps intentionally — including it causes a feedback loop
  // because its useCallback reference changes whenever localEndValue changes, which would
  // re-trigger this effect on every end date selection, causing the flickering bug.
  useEffect(() => {
    resetEndValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStartValue]);

  // Calculate total subscription charge when any relevant value changes
  // CRITICAL: Only fire callback after mount to prevent overwriting parent state
  // with initial values that may not reflect user intent
  // CRITICAL: Pass localTimeValue directly, NOT a computed value from getPayloadTimeValue()
  // Using getPayloadTimeValue() causes infinite loops because it computes different values
  // based on isNextChargeDateToday()/getCurrentNormalizedTime(), which then triggers prop changes
  // from parent, which re-triggers this effect in a loop (e.g., 'now' -> '03:00' -> 'now' -> ...)
  useEffect(() => {
    if (props.calculateTotalSubscriptionCharge && hasMountedRef.current) {
      props.calculateTotalSubscriptionCharge(
        props.fundKey,
        localStartValue,
        localEndValue,
        localTimeValue,
      );
    }
    // Note: props.calculateTotalSubscriptionCharge intentionally omitted from deps
    // to avoid infinite loops if parent doesn't memoize the callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStartValue, localEndValue, localTimeValue, props.fundKey]);

  const disabledStartDate = (current) => {
    return current && current < dayjs().startOf('day');
  };

  const disabledEndDate = (current) => {
    let startingPosition = dayjs();
    if (localStartValue !== undefined && localStartValue !== null) {
      startingPosition = dayjs(localStartValue);
    }

    if (props.timePicker === 'month') {
      // Disable current month and before — end must be at least next month
      // Compare year+month to handle Ant Design's picker="month" which passes 1st of month
      const startYearMonth = startingPosition.year() * 12 + startingPosition.month();
      const currentYearMonth = current.year() * 12 + current.month();
      return currentYearMonth <= startYearMonth;
    } else if (props.timePicker === 'week') {
      // Disable dates before start AND dates that aren't the same day-of-week
      if (current <= dayjs(startingPosition)) return true;
      return current.day() !== startingPosition.day();
    } else if (props.timePicker === 'day') {
      return current <= dayjs(startingPosition);
    } else if (props.timePicker === 'year') {
      // Disable current year and before — end must be at least next year
      return current.year() <= startingPosition.year();
    }
  };

  const clearNextChargeDate = () => {
    setLocalStartValue(undefined);
    setLocalTimeValue('now');
  };

  const clearEndChargeDate = () => {
    setLocalEndValue(undefined);
  };

  const handleTimeChange = (selectedTime) => {
    setLocalTimeValue(selectedTime);
    setShowTimeDropdown(false);
  };

  const handleResetTime = () => {
    if (!localStartValue || isNextChargeDateToday()) {
      setLocalTimeValue('now');
    } else {
      setLocalTimeValue(getCurrentNormalizedTime());
    }
  };

  // Custom Time Input Component
  const CustomTimeInput = () => {
    const displayValue = getDisplayTimeValue();

    // embed_form specific styling
    if (theme === 'embed_form') {
      const brandColors = props.brandColors || {};
      const brandAccent5 = brandColors?.['accent-5'] || '#f9fafb';

      return (
        <div className="relative  mb-2">
          <div
            role="button"
            tabIndex={0}
            className="peer flex justify-between items-center w-full px-[10px] py-2 rounded-md border shadow-[shadow:var(--cs-control-shadow)] text-base text-gray-900 placeholder-transparent focus:outline-none border-[color:var(--cs-border-control)] cursor-pointer"
            style={{ backgroundColor: brandAccent5 }}
            onClick={(e) => {
              if (!e.target.closest('button[type="button"]')) {
                setShowTimeDropdown(!showTimeDropdown);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowTimeDropdown(!showTimeDropdown);
              }
            }}
          >
            <div className="flex items-center justify-between text-sm w-full">
              <span>{displayValue}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResetTime();
                  }}
                  className="text-xs text-[color:var(--cs-text-muted)] hover:text-gray-700 underline"
                >
                  Reset
                </button>
                <ChevronDownIcon className="h-6 w-6 text-[color:var(--cs-text-muted)]" aria-hidden="true" />
              </div>
            </div>
          </div>
          {showTimeDropdown && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border border-gray-300 bg-white shadow-lg">
              <div className="max-h-60 overflow-auto">
                {isNextChargeDateToday() && (
                  <button
                    type="button"
                    onClick={() => handleTimeChange('now')}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                  >
                    now
                  </button>
                )}
                {getAvailableTimeOptions().map((time) => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => handleTimeChange(time)}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    // payment_form (default) styling
    return (
      <div className="relative">
        <button
          type="button"
          className="flex h-7 w-full items-center justify-between rounded-md border border-edge-input bg-white px-2 text-sm text-ink hover:bg-paper-inset focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage"
          onClick={() => setShowTimeDropdown(!showTimeDropdown)}
          title="Click to select a specific time"
        >
          <span>{displayValue}</span>
          <svg
            className="h-4 w-4 text-ink-muted"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showTimeDropdown && (
          <div className="absolute top-8 z-50 w-full rounded-md border border-edge bg-white shadow-lg">
            <div className="max-h-60 overflow-auto">
              {isNextChargeDateToday() && (
                <button
                  type="button"
                  onClick={() => handleTimeChange('now')}
                  className="w-full px-3 py-2 text-left text-sm text-ink-secondary hover:bg-paper-inset focus:bg-paper-inset focus:outline-none focus:ring-0"
                >
                  now
                </button>
              )}
              {getAvailableTimeOptions().map((time) => (
                <button
                  key={time}
                  type="button"
                  onClick={() => handleTimeChange(time)}
                  className="w-full px-3 py-2 text-left text-sm text-ink-secondary hover:bg-paper-inset focus:bg-paper-inset focus:outline-none focus:ring-0"
                >
                  {time}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // embed_form theme
  if (theme === 'embed_form') {
    const brandColors = props.brandColors || {};
    const brandAccent5 = brandColors?.['accent-5'] || '#f9fafb';

    return (
      <>
        <style>
          {`
            .ant-picker-input > input:hover,
            .ant-picker-input > input:focus,
            .ant-picker:hover,
            .ant-picker:focus-within {
              border-color: var(--cs-border-control) !important;
              box-shadow: var(--cs-control-shadow) !important;
            }

            .ant-picker-input > input {
              border: none !important;
              outline: none !important;
              background: transparent !important;
            }

            .ant-picker-input > input:hover,
            .ant-picker-input > input:focus {
              border: none !important;
              outline: none !important;
              background: transparent !important;
            }

            .ant-picker .ant-picker-input > input,
            .ant-picker .ant-picker-input > input:hover,
            .ant-picker .ant-picker-input > input:focus,
            .ant-picker .ant-picker-input > input:active {
              border: none !important;
              outline: none !important;
              box-shadow: none !important;
              background: transparent !important;
            }

            .ant-picker-input {
              border: none !important;
              outline: none !important;
              background: transparent !important;
            }

            .ant-picker-input:hover,
            .ant-picker-input:focus {
              border: none !important;
              outline: none !important;
              background: transparent !important;
            }
          `}
        </style>
        <div className="flex w-full flex-col bg-white rounded-md">
          <div className="w-full">
            <div className="flex gap-3 py-1">
              <div className="flex flex-col flex-1 mb-2">
                <label className="text-sm text-gray-700 pb-1">
                  {props.startDateLabel || 'Next Charge Date'}
                </label>
                <div className="relative flex items-center rounded-md shadow-sm">
                  <DatePicker
                    className="peer flex justify-between items-center w-full px-[10px] py-2 rounded-md border shadow-[shadow:var(--cs-control-shadow)] text-base text-gray-900 placeholder-transparent focus:outline-none border-[color:var(--cs-border-control)]"
                    style={{ backgroundColor: brandAccent5 }}
                    styles={{
                      input: {
                        backgroundColor: brandAccent5,
                        border: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: brandAccent5,
                          border: 'none',
                          boxShadow: 'none',
                        },
                        '&:focus': {
                          backgroundColor: brandAccent5,
                          border: 'none',
                          boxShadow: 'none',
                        },
                      },
                    }}
                    suffixIcon={null}
                    allowClear={false}
                    format={dateFormat}
                    disabledDate={disabledStartDate}
                    onChange={(date) => {
                      const newDate = dayjs(date);
                      setLocalStartValue(newDate);
                      setLocalTimeValue(getCurrentNormalizedTime());
                      setShowTimeDropdown(false);
                      if (props.setStartValue) props.setStartValue(props.fundKey, newDate);
                    }}
                    value={localStartValue}
                    showNow={false}
                    inputReadOnly={true}
                    picker={props.timePicker === 'day' ? 'date' : props.timePicker}
                  />
                  {localStartValue && (
                    <button
                      type="button"
                      onClick={clearNextChargeDate}
                      className="absolute right-2 flex items-center justify-center p-1 text-[color:var(--cs-text-muted)] transition-colors hover:text-gray-600"
                      title="Clear Next Charge Date"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-col flex-1 mb-2">
                <label className="text-sm text-gray-700 pb-1">
                  {props.endDateLabel || 'End Charge Date'}
                </label>
                <div className="relative flex items-center rounded-md shadow-sm">
                  <DatePicker
                    className="peer flex justify-between items-center w-full px-[10px] py-2 rounded-md border shadow-[shadow:var(--cs-control-shadow)] text-base text-gray-900 placeholder-transparent focus:outline-none border-[color:var(--cs-border-control)]"
                    style={{ backgroundColor: brandAccent5 }}
                    styles={{
                      input: {
                        backgroundColor: brandAccent5,
                        border: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: brandAccent5,
                          border: 'none',
                          boxShadow: 'none',
                        },
                        '&:focus': {
                          backgroundColor: brandAccent5,
                          border: 'none',
                          boxShadow: 'none',
                        },
                      },
                    }}
                    picker={props.timePicker === 'day' ? 'date' : props.timePicker}
                    suffixIcon={null}
                    allowClear={false}
                    format={dateFormat}
                    disabledDate={disabledEndDate}
                    onChange={(date) => {
                      const raw = dayjs(date);
                      const snapped = snapEndDate(raw);
                      setLocalEndValue(snapped || raw);
                    }}
                    value={localEndValue === 'INFINITY' ? undefined : localEndValue}
                    showNow={false}
                    inputReadOnly={true}
                  />
                  {localEndValue && localEndValue !== 'INFINITY' && (
                    <button
                      type="button"
                      onClick={clearEndChargeDate}
                      className="absolute right-2 flex items-center justify-center p-1 text-[color:var(--cs-text-muted)] transition-colors hover:text-gray-600"
                      title="Clear End Charge Date"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="text-sm text-gray-700 pb-1">{props.timeLabel || 'Charge Time'}</div>
            <CustomTimeInput />
          </div>
        </div>
      </>
    );
  }

  // payment_form theme (default - original working code)
  return (
    <div className="recurring-date-picker-payment-form flex w-full items-center rounded-md">
      <div className="flex w-full flex-col items-center gap-4 rounded-md">
        <div className="flex w-full items-center gap-2 rounded-md">
          <div className="flex w-1/2 flex-col">
            <label
              htmlFor={`fund-next-charge-date-${props.fundKey}`}
              className="block font-inter text-sm font-medium leading-5 text-ink-muted"
            >
              Next Charge Date
            </label>
            <div
              className={`flex w-full items-center rounded-md border ${
              // An equality test against a caller's colour prop, not a paint: swapping in
              // var() would compare a string to a literal and always be false.
              // cs-allow-hardcoded: compared as a string, never rendered as a colour
              props.lightColor === '#ef4444'
                  ? 'border-status-red-text bg-white'
                  : 'border-edge-input bg-white'
              } focus-within:border-sage focus-within:ring-2 focus-within:ring-sage`}
            >
              <DatePicker
                key={localEndValue}
                style={{
                  border: 0,
                  padding: '2px 8px 2px',
                  flexGrow: 2,
                  boxShadow: '0 0 0 0',
                }}
                suffixIcon={null}
                allowClear={false}
                format={dateFormat}
                disabledDate={disabledStartDate}
                onChange={(date) => {
                  setLocalStartValue(dayjs(date));
                  setLocalTimeValue(getCurrentNormalizedTime());
                  setShowTimeDropdown(false);
                }}
                value={localStartValue}
                showNow={false}
                inputReadOnly={true}
                getPopupContainer={(trigger) => {
                  // ONLY for payment_form: Find the closest parent with pointer-events-auto
                  // This fixes the issue where DatePicker is inside a Drawer with pointer-events-none
                  let parent = trigger.parentElement;
                  while (parent) {
                    const style = window.getComputedStyle(parent);
                    if (style.pointerEvents === 'auto' || parent === document.body) {
                      return parent;
                    }
                    parent = parent.parentElement;
                  }
                  return document.body;
                }}
              />
              {localStartValue && (
                <button
                  type="button"
                  onClick={clearNextChargeDate}
                  className="flex items-center justify-center p-1 text-ink-muted transition-colors hover:text-ink-secondary focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-sage"
                  title="Clear Next Charge Date"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex w-1/2 flex-col">
            <label
              htmlFor={`fund-end-charge-date-${props.fundKey}`}
              className="block font-inter text-sm font-medium leading-5 text-ink-muted"
            >
              End Charge Date
            </label>
            <div className="flex w-full items-center rounded-md border border-edge-input bg-white focus-within:border-sage focus-within:ring-2 focus-within:ring-sage">
              <DatePicker
                key={localStartValue}
                style={{
                  border: 0,
                  flexGrow: 1,
                  padding: '2px 8px 2px',
                  boxShadow: '0 0 0 0',
                }}
                picker={props.timePicker === 'day' ? 'date' : props.timePicker}
                suffixIcon={null}
                allowClear={false}
                format={dateFormat}
                disabledDate={disabledEndDate}
                onChange={(date) => {
                  const raw = dayjs(date);
                  const snapped = snapEndDate(raw);
                  setLocalEndValue(snapped || raw);
                }}
                value={localEndValue === 'INFINITY' ? undefined : localEndValue}
                showNow={false}
                inputReadOnly={true}
                getPopupContainer={(trigger) => {
                  // ONLY for payment_form: Find the closest parent with pointer-events-auto
                  let parent = trigger.parentElement;
                  while (parent) {
                    const style = window.getComputedStyle(parent);
                    if (style.pointerEvents === 'auto' || parent === document.body) {
                      return parent;
                    }
                    parent = parent.parentElement;
                  }
                  return document.body;
                }}
              />
              {localEndValue === 'INFINITY' && (
                <span className="px-2 text-sm text-ink-muted">INFINITY</span>
              )}
              {localEndValue && localEndValue !== 'INFINITY' && (
                <button
                  type="button"
                  onClick={clearEndChargeDate}
                  className="flex items-center justify-center p-1 text-ink-muted transition-colors hover:text-ink-secondary focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-sage"
                  title="Clear End Charge Date"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex w-full flex-col gap-1">
          <label
            htmlFor={`fund-charge-time-${props.fundKey}`}
            className="block font-inter text-sm font-medium leading-5 text-ink-muted"
          >
            Charge Time
          </label>
          <div className="relative w-full">
            <CustomTimeInput />
            <button
              type="button"
              onClick={handleResetTime}
              className="absolute right-8 top-1/2 -translate-y-1/2 p-1 text-ink-muted hover:text-ink-secondary focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-sage"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecurringDatePicker;
